import { msg } from "@lingui/core/macro";
import { createRoute } from "@tanstack/solid-router";
import { createMemo, createSignal, For, Show } from "solid-js";
import { Trash2, TrendingUp } from "lucide-solid";
import {
  computeReplenishmentSuggestions,
  createReorderRuleMutation,
  deleteReorderRuleMutation,
  fromMinorQty,
  getLocationsQuery,
  getProductsQuery,
  getReorderRulesQuery,
  getStockQuantsQuery,
  getWarehousesQuery,
  parseQtyInput,
  queryKeys,
} from "@stockpilot/shared";
import { createPowerSyncQuery } from "@stockpilot/shared/solid";
import { appRoute } from "./route";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useOrgId } from "@/features/inventory/use-org-id";
import { PageHeader } from "@/components/ui/page-header";
import { PageContainer } from "@/components/ui/page-container";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Select } from "@/components/ui/select";
import { SwipeRow } from "@/components/ui/swipe-row";
import type {
  LocationRecord,
  ProductRecord,
  ReorderRuleRecord,
  StockQuantRecord,
  WarehouseRecord,
} from "@stockpilot/powersync";

function ReplenishmentPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const orgId = useOrgId();

  const [ruleProduct, setRuleProduct] = createSignal("");
  const [ruleWarehouse, setRuleWarehouse] = createSignal("");
  const [minQty, setMinQty] = createSignal("");
  const [maxQty, setMaxQty] = createSignal("");

  const products = createPowerSyncQuery<ProductRecord>({
    queryKey: () => queryKeys.inventory.products(orgId()),
    queryFn: () => getProductsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const warehouses = createPowerSyncQuery<WarehouseRecord>({
    queryKey: () => queryKeys.inventory.warehouses(orgId()),
    queryFn: () => getWarehousesQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const locations = createPowerSyncQuery<LocationRecord>({
    queryKey: () => queryKeys.inventory.locations(orgId()),
    queryFn: () => getLocationsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const quants = createPowerSyncQuery<StockQuantRecord>({
    queryKey: () => queryKeys.inventory.quants(orgId()),
    queryFn: () => getStockQuantsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const rules = createPowerSyncQuery<ReorderRuleRecord>({
    queryKey: () => queryKeys.inventory.reorderRules(orgId()),
    queryFn: () => getReorderRulesQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const productName = createMemo(() => {
    const map = new Map((products.data ?? []).map((p) => [p.id, p.name]));
    return (id: string) => map.get(id) ?? id;
  });
  const warehouseName = createMemo(() => {
    const map = new Map((warehouses.data ?? []).map((w) => [w.id, w.name]));
    return (id: string | null) => (id ? (map.get(id) ?? id) : t(msg`All warehouses`));
  });

  // Mapa ubicación → almacén, para imputar el on-hand por almacén.
  const locationWarehouse = createMemo(() => {
    const map = new Map((locations.data ?? []).map((l) => [l.id, l.warehouse_id]));
    return map;
  });

  // On-hand por (producto, almacén) y total por producto, en unidades menores.
  const onHandMaps = createMemo(() => {
    const byKey = new Map<string, number>();
    const byProduct = new Map<string, number>();
    const locWh = locationWarehouse();
    for (const q of quants.data ?? []) {
      const pid = q.product_id;
      if (!pid) {
        continue;
      }
      const qty = q.qty ?? 0;
      byProduct.set(pid, (byProduct.get(pid) ?? 0) + qty);
      const wh = locWh.get(q.location_id ?? "") ?? null;
      if (wh) {
        const key = `${pid} ${wh}`;
        byKey.set(key, (byKey.get(key) ?? 0) + qty);
      }
    }
    return { byKey, byProduct };
  });

  // Sugerencias deterministas (lógica pura, sin AI).
  const suggestions = createMemo(() => {
    const { byKey, byProduct } = onHandMaps();
    return computeReplenishmentSuggestions(rules.data ?? [], byKey, byProduct);
  });

  async function handleCreateRule(e: Event) {
    e.preventDefault();
    const d = drizzle();
    const pid = ruleProduct();
    const min = parseQtyInput(minQty());
    const max = parseQtyInput(maxQty());
    if (!d || !pid || min === null) {
      return;
    }
    await createReorderRuleMutation(d, orgId(), {
      productId: pid,
      warehouseId: ruleWarehouse() || null,
      minQty: min,
      maxQty: max ?? min,
    });
    setRuleProduct("");
    setRuleWarehouse("");
    setMinQty("");
    setMaxQty("");
  }

  async function handleDeleteRule(id: string) {
    const d = drizzle();
    if (!d) {
      return;
    }
    await deleteReorderRuleMutation(d, id);
  }

  return (
    <PageContainer>
      <PageHeader
        title={t(msg`Replenishment`)}
        description={t(msg`Reorder rules and low-stock suggestions.`)}
        icon={TrendingUp}
      />

      <form
        class="mt-6 flex flex-wrap items-end gap-2 rounded-md border border-border p-4"
        onSubmit={handleCreateRule}
        data-testid="rule-form"
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Product`)}</label>
          <Select
            class="w-44"
            value={ruleProduct()}
            onChange={(v) => setRuleProduct(v)}
            data-testid="rule-product-select"
            aria-label={t(msg`Product`)}
            placeholder={t(msg`Select product`)}
            options={(products.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Warehouse`)}</label>
          <Select
            class="w-44"
            value={ruleWarehouse()}
            onChange={(v) => setRuleWarehouse(v)}
            data-testid="rule-warehouse-select"
            aria-label={t(msg`Warehouse`)}
            options={[
              { value: "", label: t(msg`All warehouses`) },
              ...(warehouses.data ?? []).map((w) => ({ value: w.id, label: w.name })),
            ]}
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Minimum`)}</label>
          <input
            class="w-24 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
            placeholder="0"
            inputmode="decimal"
            value={minQty()}
            onInput={(e) => setMinQty(e.currentTarget.value)}
            data-testid="rule-min-input"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-muted-foreground">{t(msg`Maximum`)}</label>
          <input
            class="w-24 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
            placeholder="0"
            inputmode="decimal"
            value={maxQty()}
            onInput={(e) => setMaxQty(e.currentTarget.value)}
            data-testid="rule-max-input"
          />
        </div>
        <Button type="submit" disabled={!drizzle()} data-testid="rule-add-button">
          {t(msg`Add rule`)}
        </Button>
      </form>

      {/* Sugerencias de reabastecimiento (productos bajo mínimo). */}
      <h2 class="mt-8 text-lg font-semibold">{t(msg`Replenishment suggestions`)}</h2>
      <div class="mt-3" data-testid="suggestion-list">
        <Show
          when={suggestions().length > 0}
          fallback={
            <EmptyState
              icon={TrendingUp}
              title={t(msg`Nothing to replenish`)}
              description={t(msg`No product is below its minimum stock level.`)}
            />
          }
        >
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border text-left text-muted-foreground">
                <th class="py-2 font-medium">{t(msg`Product`)}</th>
                <th class="py-2 font-medium">{t(msg`Warehouse`)}</th>
                <th class="py-2 font-medium text-right">{t(msg`On-hand`)}</th>
                <th class="py-2 font-medium text-right">{t(msg`Minimum`)}</th>
                <th class="py-2 font-medium text-right">{t(msg`To order`)}</th>
              </tr>
            </thead>
            <tbody>
              <For each={suggestions()}>
                {(s) => (
                  <tr
                    class="border-b border-border/50"
                    data-testid="suggestion-row"
                    data-product-id={s.productId}
                  >
                    <td class="py-2">{productName()(s.productId)}</td>
                    <td class="py-2">{warehouseName()(s.warehouseId)}</td>
                    <td class="py-2 text-right tabular-nums">{fromMinorQty(s.onHand)}</td>
                    <td class="py-2 text-right tabular-nums">{fromMinorQty(s.minQty)}</td>
                    <td
                      class="py-2 text-right tabular-nums font-medium"
                      data-testid="suggestion-qty"
                    >
                      {fromMinorQty(s.suggestedQty)}
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </Show>
      </div>

      {/* Reglas configuradas. */}
      <h2 class="mt-8 text-lg font-semibold">{t(msg`Reorder rules`)}</h2>
      <div class="mt-3" data-testid="rule-list">
        <Show
          when={(rules.data ?? []).length > 0}
          fallback={<p class="text-sm text-muted-foreground">{t(msg`No rules yet.`)}</p>}
        >
          <ul class="divide-y divide-border rounded-md border border-border">
            <For each={rules.data ?? []}>
              {(r) => (
                <li data-testid="rule-row" data-product-id={r.product_id ?? ""}>
                  <SwipeRow
                    left={{
                      icon: Trash2,
                      label: t(msg`Delete`),
                      tone: "danger",
                      exits: true,
                      onCommit: () => void handleDeleteRule(r.id),
                    }}
                  >
                    <div class="flex items-center gap-3 px-4 py-3">
                      <div class="flex-1 min-w-0">
                        <p class="font-medium">{productName()(r.product_id ?? "")}</p>
                        <p class="text-xs text-muted-foreground">
                          {warehouseName()(r.warehouse_id)} · {t(msg`Min`)}{" "}
                          {fromMinorQty(r.min_qty ?? 0)} · {t(msg`Max`)}{" "}
                          {fromMinorQty(r.max_qty ?? 0)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void handleDeleteRule(r.id)}
                        data-testid="rule-delete-button"
                      >
                        {t(msg`Delete`)}
                      </Button>
                    </div>
                  </SwipeRow>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>
    </PageContainer>
  );
}

export const replenishmentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/replenishment",
  component: ReplenishmentPage,
});
